import React, { useState } from 'react';
import { Badge, Row, Col, ProgressBar } from 'react-bootstrap';
import { 
  FaChevronDown, 
  FaChevronUp, 
  FaTools, 
  FaRocket, 
  FaClock,
  FaStore,
  FaSync,
  FaCog,
  FaChartLine,
  FaBell,
  FaStar
} from 'react-icons/fa';
import MiniLoadingSpinner from '../../../../../../../../components/Spinners/MiniLoadingSpinner';
import './Uber.scss';

const Uber: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);

  const handleNotificationRequest = async () => {
    setNotificationLoading(true);
    // Simulate API call
    setTimeout(() => {
      setNotificationLoading(false);
    }, 2000);
  };

  const renderProviderLogo = () => {
    return(
      <div className="provider-logo-small">
        <img 
          alt="Uber Eats logo"
          className="provider-logo-img"
          src={`${process.env.PUBLIC_URL}/icons/providers/uber-eats.png`} 
        />
      </div>
    )
  }

  const developmentFeatures = [
    {
      icon: <FaStore />,
      title: 'Gestión de Tienda',
      description: 'Abrir/cerrar tienda automáticamente',
      status: 'planned',
      progress: 0
    },
    {
      icon: <FaSync />,
      title: 'Actualización de Menú',
      description: 'Sincronización automática de catálogo',
      status: 'in-progress',
      progress: 30
    },
    {
      icon: <FaCog />,
      title: 'Configuración de API',
      description: 'Gestión de credenciales y endpoints',
      status: 'planned',
      progress: 0
    },
    {
      icon: <FaChartLine />,
      title: 'Disponibilidad de Productos',
      description: 'Control de stock en tiempo real',
      status: 'planned',
      progress: 0
    },
    {
      icon: <FaBell />,
      title: 'Órdenes en Tiempo Real',
      description: 'Notificaciones y gestión de pedidos',
      status: 'research',
      progress: 10
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in-progress': return 'warning';
      case 'research': return 'info';
      default: return 'secondary';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return 'Completado';
      case 'in-progress': return 'En Desarrollo';
      case 'research': return 'Investigación';
      default: return 'Planificado';
    }
  };

  return (
    <div className="uber-card">
      <div 
        className="uber-header"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ cursor: 'pointer' }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            {renderProviderLogo()}
            <div className="ms-4">
              <h4 className="mb-0">Uber Eats</h4>
              <small className="text-white-50">Integración de delivery</small>
            </div>
          </div>
          <div className="d-flex align-items-center">
            <Badge bg="warning" className="me-2 pulse-badge">
              <FaRocket className="me-1" />
              En Desarrollo
            </Badge>
            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
          </div>
        </div>
      </div>
      
      {isExpanded && (
        <div className="uber-body">
          <div className="development-status-banner">
            <Row className="align-items-center">
              <Col md={9}>
                <div className="d-flex align-items-center">
                  <FaTools className="me-2 development-icon" size={24} />
                  <div>
                    <h6 className="mb-1">Integración en Desarrollo Activo</h6>
                    <small className="text-muted">
                      Estamos trabajando en la integración completa con Uber Eats.
                    </small>
                  </div>
                </div>
              </Col>
              <Col md={3} className="text-end">
                <div className="development-progress">
                  <small className="text-muted d-block mb-1">15% Completado</small>
                  <ProgressBar 
                    now={15} 
                    variant="warning" 
                    style={{ height: '6px' }}
                  />
                </div>
              </Col>
            </Row>
          </div>

          <div className="features-roadmap">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="mb-0 text-muted">
                <FaStar className="me-2" />
                Funcionalidades Planificadas
              </h6>
              <small className="text-muted">Q3-Q4 2025</small>
            </div>
            
            <Row>
              {developmentFeatures.map((feature, index) => (
                <Col md={6} key={index} className="mb-2">
                  <div className="feature-item">
                    <div className="d-flex align-items-center">
                      <div className="feature-icon me-2">
                        {feature.icon}
                      </div>
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="fw-medium">{feature.title}</small>
                          <Badge 
                            bg={getStatusColor(feature.status)}
                            className="status-badge"
                          >
                            {getStatusText(feature.status)}
                          </Badge>
                        </div>
                        <small className="text-muted d-block">{feature.description}</small>
                        {feature.progress > 0 && (
                          <ProgressBar 
                            now={feature.progress} 
                            variant={getStatusColor(feature.status)}
                            style={{ height: '3px' }}
                            className="mt-1"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </div>

          <div className="timeline-section mt-3">
            <h6 className="mb-2 text-muted">
              <FaClock className="me-2" />
              Cronograma
            </h6>
            <div className="timeline-compact">
              <div className="timeline-item">
                <div className="timeline-marker current"></div>
                <div className="timeline-content">
                  <small className="fw-medium">Q3 2025 - Investigación</small>
                  <small className="text-muted d-block">Análisis de APIs y configuración inicial</small>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-marker"></div>
                <div className="timeline-content">
                  <small className="fw-medium">Q4 2025 - Desarrollo Core</small>
                  <small className="text-muted d-block">Gestión de tienda y menú</small>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-marker"></div>
                <div className="timeline-content">
                  <small className="fw-medium">Q1 2026 - Funcionalidades Avanzadas</small>
                  <small className="text-muted d-block">Órdenes en tiempo real</small>
                </div>
              </div>
            </div>
          </div>

          <div className="cta-section mt-3 p-2 text-center">
            <small className="text-muted d-block mb-2">
              ¿Interesado en esta integración? Te notificaremos cuando esté disponible.
            </small>
            <button 
              className="mp-button secondary" 
              onClick={handleNotificationRequest}
              disabled={notificationLoading}
            >
              {notificationLoading ? (
                <MiniLoadingSpinner />
              ) : (
                <>
                  <FaBell className="me-1" />
                  Notificarme
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Uber;